<h2 mat-dialog-title class="preview-title">{{ data.name }}</h2>

<mat-dialog-content class="preview-content">
	<div class="preview-area">
		@if (loading()) {
			<div class="preview-state">
				<mat-spinner diameter="48"></mat-spinner>
			</div>
		} @else if (error()) {
			<div class="preview-state">
				<p>Nie udało się załadować zawartości skanu.</p>
			</div>
		} @else if (isImage()) {
			<div class="preview-image-branch">
				<div class="preview-toolbar">
					<button
						mat-icon-button
						type="button"
						matTooltip="Pomniejsz"
						aria-label="Pomniejsz"
						(click)="zoomOut()"
					>
						<mat-icon><i class="fa-solid fa-magnifying-glass-minus"></i></mat-icon>
					</button>
					<span class="preview-zoom" aria-live="polite">{{ zoomPercent() }}</span>
					<button
						mat-icon-button
						type="button"
						matTooltip="Powiększ"
						aria-label="Powiększ"
						(click)="zoomIn()"
					>
						<mat-icon><i class="fa-solid fa-magnifying-glass-plus"></i></mat-icon>
					</button>
					<span class="preview-toolbar-divider" aria-hidden="true"></span>
					<button
						mat-icon-button
						type="button"
						matTooltip="Obróć w lewo"
						aria-label="Obróć w lewo"
						(click)="rotateLeft()"
					>
						<mat-icon><i class="fa-solid fa-rotate-left"></i></mat-icon>
					</button>
					<button
						mat-icon-button
						type="button"
						matTooltip="Obróć w prawo"
						aria-label="Obróć w prawo"
						(click)="rotateRight()"
					>
						<mat-icon><i class="fa-solid fa-rotate-right"></i></mat-icon>
					</button>
					<button
						mat-icon-button
						type="button"
						matTooltip="Dopasuj do okna"
						aria-label="Dopasuj do okna"
						(click)="resetView()"
					>
						<mat-icon><i class="fa-solid fa-expand"></i></mat-icon>
					</button>
				</div>
				<div
					#viewport
					class="preview-viewport"
					(wheel)="onWheel($event)"
					(pointerdown)="onPointerDown($event)"
					(pointermove)="onPointerMove($event)"
					(pointerup)="onPointerUp()"
					(pointercancel)="onPointerUp()"
					(pointerleave)="onPointerUp()"
				>
					<img
						class="preview-image"
						[class.is-zoomed]="zoom() > 1"
						[src]="imageSrc()"
						[alt]="data.name"
						[style.transform]="transform()"
						(load)="onImageLoad($event)"
						draggable="false"
					/>
				</div>
				<p class="preview-caption" [title]="data.name">{{ data.name }}</p>
			</div>
		} @else if (isPdf()) {
			<iframe class="preview-pdf" [src]="pdfSrc()" [title]="data.name"></iframe>
		} @else {
			<div class="preview-state">
				<p>Podgląd niedostępny dla tego typu pliku.</p>
			</div>
		}
	</div>

	@if (scan(); as currentScan) {
		<aside class="cost-panel" aria-label="Dane z OCR">
			<div class="cost-form">
				<h3 class="cost-panel-title">Dane kosztu</h3>

				<section class="cost-section">
					<div class="cost-section-header">
						<span class="cost-section-dot"></span>
						<span class="cost-section-label">Dokument</span>
					</div>

					<div class="cost-identity">
						<a
							class="cost-doc-link"
							[routerLink]="['/tms/box-list', data.boxId]"
							target="_blank"
							rel="noopener noreferrer"
						>
							<mat-icon><i class="fa-solid fa-box-archive"></i></mat-icon>
							<span>BOX #{{ data.boxId }}</span>
							<mat-icon class="cost-doc-link-ext"
								><i class="fa-solid fa-arrow-up-right-from-square"></i
							></mat-icon>
						</a>
						@if (currentScan.updated_at) {
							<span class="cost-identity-edited">
								edytowano {{ currentScan.updated_at | formatDateTime }}
								@if (currentScan.updated_by) {
									· {{ currentScan.updated_by }}
								}
							</span>
						}
					</div>

					<div
						class="ai-suggestions"
						[class.ai-suggestions--quiet]="currentScan.ocrStatus !== 'completed'"
						role="region"
						aria-label="Status OCR"
					>
						<div class="ai-suggestions-header">
							<div class="ai-suggestions-title">
								<mat-icon><i class="fa-solid fa-wand-magic-sparkles"></i></mat-icon>
								<span>Analiza AI</span>
								<span class="ocr-status-chip" [class]="ocrStatusMeta[currentScan.ocrStatus].cssClass">
									<i class="fa-solid {{ ocrStatusMeta[currentScan.ocrStatus].icon }}"></i>
									{{ ocrStatusMeta[currentScan.ocrStatus].label }}
								</span>
							</div>
							<span
								class="ocr-recompute-wrap"
								[matTooltip]="
									currentScan.archiveStatus !== 'archived'
										? 'Skan nie został jeszcze zarchiwizowany — OCR uruchomi się dopiero po archiwizacji.'
										: 'Wymusza ponowne przeliczenie OCR na podstawie skanu.'
								"
								matTooltipPosition="above"
							>
								<button
									mat-stroked-button
									type="button"
									class="ocr-recompute-btn"
									(click)="recomputeOcr()"
									[disabled]="recomputingOcr() || currentScan.archiveStatus !== 'archived'"
								>
									@if (recomputingOcr()) {
										<mat-spinner diameter="16"></mat-spinner>
									} @else {
										<mat-icon><i class="fa-solid fa-arrows-rotate"></i></mat-icon>
									}
									<span>Przelicz OCR</span>
								</button>
							</span>
						</div>

						@if (recomputeOcrError(); as ocrErr) {
							<p class="cost-hint cost-hint--error">{{ ocrErr }}</p>
						} @else if (currentScan.ocrStatus === 'failed' && currentScan.ocrError) {
							<p class="cost-hint cost-hint--error">{{ currentScan.ocrError }}</p>
						}
					</div>
				</section>

				<section class="cost-section">
					<div class="cost-section-header">
						<span class="cost-section-dot"></span>
						<span class="cost-section-label">Opis</span>
					</div>

					@if (summaryText()) {
						<p class="ai-suggestions-summary">{{ summaryText() }}</p>
					}
					@if (currentScan.documentNumber || currentScan.documentDate || currentScan.supplierName) {
						<dl class="ai-suggestions-meta">
							@if (currentScan.documentNumber) {
								<dt>Numer dokumentu</dt>
								<dd>{{ currentScan.documentNumber }}</dd>
							}
							@if (currentScan.documentDate) {
								<dt>Data dokumentu</dt>
								<dd>{{ currentScan.documentDate }}</dd>
							}
							@if (currentScan.supplierName) {
								<dt>Dostawca</dt>
								<dd>{{ currentScan.supplierName }}</dd>
							}
						</dl>
					}
					@if (
						!summaryText() &&
						!currentScan.documentNumber &&
						!currentScan.documentDate &&
						!currentScan.supplierName
					) {
						<p class="cost-hint">
							Brak opisu z OCR — uruchom „Przelicz OCR”, aby wygenerować podsumowanie, numer dokumentu,
							datę i dostawcę.
						</p>
					}
				</section>
			</div>
		</aside>
	}
</mat-dialog-content>

<mat-dialog-actions align="end">
	@if (!loading() && !error()) {
		<button mat-stroked-button type="button" (click)="download()">
			<mat-icon><i class="fa-solid fa-download"></i></mat-icon>
			<span>Pobierz</span>
		</button>
	}
	<button mat-button type="button" (click)="close()">Zamknij</button>
</mat-dialog-actions>
